import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import {
    IconButton,
    Badge,
    Popover,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    Box,
    Divider,
    Chip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';

const NOTIFICATION_CONFIG = {
    integration_sync_error: {
        label: 'Error',
        color: 'error',
        navigationPath: '/settings/integrations'
    },
    unlinked_customers_error: {
        label: 'Warning', 
        color: 'warning',
        navigationPath: '/customers-table'
    },
    unlinked_products_error: {
        label: 'Warning',
        color: 'warning', 
        navigationPath: '/products-table'
    },
    success: {
        label: 'Success',
        color: 'success',
        navigationPath: null
    },
    warning: {
        label: 'Warning',
        color: 'warning',
        navigationPath: null
    },
    info: {
        label: 'Info',
        color: 'info',
        navigationPath: null
    }
};

const NotificationBell = forwardRef((props, ref) => {
    const [notifications, setNotifications] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const intervalRef = useRef(null);
    const navigate = useNavigate();

    const fetchNotifications = async () => {
        try {
            const response = await axiosInstance.get('/dashboard/notifications');
            setNotifications(response.data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setNotifications([]);
        }
    };

    useImperativeHandle(ref, () => ({
        refreshNotifications: fetchNotifications
    }));

    useEffect(() => {
        fetchNotifications();

        intervalRef.current = setInterval(fetchNotifications, 5 * 60 * 1000); // 5 mins

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationClick = (notification) => {
        const config = NOTIFICATION_CONFIG[notification.type];
        if (config?.navigationPath) {
            navigate(config.navigationPath);
        }
        handleClose();
    };

    const formatTimeAgo = (timestamp) => {
        if (!timestamp)
            return null;
        
        const now = new Date();
        const notificationTime = new Date(timestamp);
        const diff = now - notificationTime;
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days > 0) return `${days}d ago`;
        if (hours > 0) return `${hours}h ago`;
        if (minutes > 0) return `${minutes}m ago`;
        return 'Just now';
    };

    const getNotificationConfig = (type) => {
        return NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.info;
    };

    const notificationCount = notifications.length;
    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleClick}
                sx={{ mr: 1 }}
            >
                <Badge badgeContent={notificationCount} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 400,
                            maxHeight: 500,
                            overflow: 'auto'
                        }
                    }
                }}
            >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6">
                        Notifications
                        {notificationCount > 0 && (
                            <Chip
                                label={notificationCount}
                                size="small"
                                color="error"
                                sx={{ ml: 1 }}
                            />
                        )}
                    </Typography>
                </Box>

                {notifications.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No notifications
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {notifications.map((notification, index) => (
                            <React.Fragment key={notification.id}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => handleNotificationClick(notification)}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography
                                                        component="span"
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            flex: 1
                                                        }}
                                                    >
                                                        {notification.title}
                                                    </Typography>
                                                    <Chip
                                                        label={getNotificationConfig(notification.type).label}
                                                        size="small"
                                                        color={getNotificationConfig(notification.type).color}
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box component="span">
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mb: 0.5, display: 'block' }}
                                                    >
                                                        {notification.message}
                                                    </Typography>
                                                    {formatTimeAgo(notification.timestamp) && (
                                                        <Typography
                                                            component="span"
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {formatTimeAgo(notification.timestamp)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                                {index < notifications.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Popover>
        </>
    );
});

NotificationBell.displayName = 'NotificationBell';

export default NotificationBell; 